/* Le prestazioni nuove da rivedere: la coda di chi fattura.
 *
 * Dal 16 ottobre 2026. Chi imputa propone una prestazione che non trova (`ProponiPrestazione`) e la usa
 * subito; qui chi fattura la guarda una volta sola e fa due cose: controlla il nome e il tipo di lavoro, e
 * scrive **la frase che finisce in fattura**. La frase e' obbligatoria: e' l'unica parte che legge il cliente,
 * e finche' non c'e' nessun avviso con quella prestazione si puo' creare (lo dice il server).
 *
 * Non si unisce un doppione a un'altra prestazione: le ore gia' registrate dovrebbero cambiare codice, e il
 * valore di quelle non fatturate cambierebbe se l'altra ha un prezzo fisso. Un doppione si rinomina.
 */

import { useEffect, useState, type FormEvent } from "react";
import { ErroreApi, anagrafica } from "../api";
import { useCarica } from "../carica";
import {
  TIPI_PRATICA,
  conta,
  dataCompleta,
  nomeTipo,
  oreEMinuti,
  type CodiceDaRivedere,
  type TipoPratica,
} from "../dominio";
import { Interruttore } from "./Interruttore";

/** Chi vuole portare l'attenzione sulla revisione (per esempio il messaggio di un avviso bloccato) lancia
 * questo evento: la banda si apre e scorre fino a se'. */
export const APRI_PRESTAZIONI_DA_RIVEDERE = "fatturazione:apri-prestazioni-da-rivedere";

/** Una banda sola in cima a "Da fatturare": dice quante sono e si apre solo quando serve. Aperta, ogni
 * prestazione e' una riga che si espande: la prima gia' aperta, le altre a un clic. */
export function CodiciDaRivedere({ dopo }: { dopo?: () => Promise<void> }) {
  const codici = useCarica<CodiceDaRivedere[]>(() => anagrafica.daRivedere(), []);
  const [aperta, setAperta] = useState(false);

  useEffect(() => {
    const apri = () => {
      setAperta(true);
      requestAnimationFrame(() =>
        document.getElementById("prestazioni-da-rivedere")?.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    };
    window.addEventListener(APRI_PRESTAZIONI_DA_RIVEDERE, apri);
    return () => window.removeEventListener(APRI_PRESTAZIONI_DA_RIVEDERE, apri);
  }, []);

  if (codici.errore) {
    return (
      <p className="errore" role="alert">
        {codici.errore}
      </p>
    );
  }
  if (codici.dati === undefined || codici.dati.length === 0) return null;
  return (
    <section id="prestazioni-da-rivedere" className="banda-da-rivedere codici-da-rivedere" aria-label="Prestazioni nuove da rivedere">
      <div className="banda-testa">
        <p>
          <strong>{conta(codici.dati.length, "prestazione nuova", "prestazioni nuove")} da rivedere.</strong>{" "}
          Le hanno create i colleghi che non la trovavano: funzionano già, ma l'avviso che le contiene non si crea
          finché non scegli la frase che compare in fattura.
        </p>
        <button type="button" className="tasto" aria-expanded={aperta} onClick={() => setAperta((a) => !a)}>
          {aperta ? "Chiudi" : "Rivedi ora"}
        </button>
      </div>
      {aperta &&
        codici.dati.map((c, indice) => (
          <Voce
            key={c.codice}
            codice={c}
            apertaDiPartenza={indice === 0}
            fatto={async () => {
              await codici.ricarica();
              await dopo?.();
            }}
          />
        ))}
    </section>
  );
}

function Voce({
  codice: c,
  fatto,
  apertaDiPartenza,
}: {
  codice: CodiceDaRivedere;
  fatto: () => Promise<void>;
  apertaDiPartenza: boolean;
}) {
  const [descrizione, setDescrizione] = useState(c.descrizione);
  const [frase, setFrase] = useState("");
  const [tipi, setTipi] = useState<string[]>(c.tipi_pratica);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const fraseScritta = frase.trim().length > 0;
  const idFrase = `frase-${c.codice}`;

  async function conferma(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    if (!fraseScritta) return setErrore("Scrivi la frase che compare in fattura.");
    if (tipi.length === 0) return setErrore("Scegli almeno un tipo di lavoro.");
    setInCorso(true);
    try {
      await anagrafica.rivedi(c.codice, { descrizione, testo_fattura: frase, tipi_pratica: tipi });
      await fatto();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <details className="codice-da-rivedere" open={apertaDiPartenza}>
      <summary>
        <strong>{c.descrizione}</strong>{" "}
        <span className="narrativa">
          proposta da {c.proposto_da} il {dataCompleta(c.proposto_il.slice(0, 10))}
          {c.imputazioni > 0 ? `, già ${conta(c.imputazioni, "ora registrata", "ore registrate")} (${oreEMinuti(c.minuti)})` : ""}
        </span>
      </summary>
      <form onSubmit={conferma} noValidate>

      {c.simili.length > 0 && (
        <p className="riga-avviso">
          Somiglia a: {c.simili.map((s) => `«${s.descrizione}»`).join(", ")}. Se è la stessa cosa, rinominala in modo che si
          distingua; le due non si possono unire.
        </p>
      )}

      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor={`desc-${c.codice}`}>Nome nell'elenco</label>
          <input id={`desc-${c.codice}`} value={descrizione} maxLength={300} onChange={(e) => setDescrizione(e.target.value)} />
        </div>
      </div>

      <fieldset className="interruttori interruttori-fitti">
        <legend>Per quali tipi di lavoro vale</legend>
        {TIPI_PRATICA.map((t) => (
          <Interruttore
            key={t.valore}
            titolo={t.nome}
            acceso={tipi.includes(t.valore)}
            cambia={() =>
              setTipi((attuali) => (attuali.includes(t.valore) ? attuali.filter((x) => x !== t.valore) : [...attuali, t.valore]))
            }
            corto
          />
        ))}
      </fieldset>

      <div className="campo">
        <label htmlFor={idFrase}>Come compare nella fattura</label>
        <input
          id={idFrase}
          value={frase}
          maxLength={500}
          placeholder="Predisposizione ed invio telematico dichiarazione IVA annuale"
          aria-describedby={`${idFrase}-aiuto`}
          onChange={(e) => setFrase(e.target.value)}
        />
        <p className="aiuto" id={`${idFrase}-aiuto`}>
          {fraseScritta ? (
            <>Nel testo da incollare: «- {frase.trim().replace(/[;.,: ]+$/, "")};»</>
          ) : (
            <>
              È la frase che legge il cliente.{" "}
              <button type="button" className="tasto tasto-nudo" onClick={() => setFrase(descrizione)}>
                Usa il nome
              </button>
            </>
          )}
        </p>
      </div>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <div>
        <button className="tasto tasto-principale" type="submit" disabled={inCorso || !fraseScritta}>
          {inCorso ? "Sto salvando" : "Conferma"}
        </button>
        <span className="narrativa">
          {" "}
          Tipi: {tipi.map((t) => nomeTipo(t as TipoPratica)).join(", ") || "nessuno"}
        </span>
      </div>
      </form>
    </details>
  );
}
